import { RefreshCw, UserPlus } from 'lucide-react'
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError, apiErrorMessage, setAdminToken } from '../../api/client'
import { adminApi } from '../../api/endpoints'
import { ErrorMessage } from '../../components/ErrorMessage'
import type { AdminRole, AdminUser } from '../../types/api'
import { AdminHeader, ROLE_LABEL } from './adminAccess'

const ROLES: AdminRole[] = ['SUPER_ADMIN', 'ADMIN', 'VERIFIER', 'UPLOADER']

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'Never'

/**
 * The ops team, for a super admin: add a person with a role, change someone's role, disable or
 * re-enable them. There are no passwords: everyone signs in with a join code emailed to them. The server refuses changing your own role, disabling
 * yourself, and leaving the console without an active super admin.
 */
export function AdminTeamPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<AdminUser[]>([])
  const [meId, setMeId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  // The row whose email is being edited, and the new address typed so far.
  const [emailFor, setEmailFor] = useState<string | null>(null)
  const [newEmail, setNewEmail] = useState('')

  const [form, setForm] = useState({ fullName: '', email: '', role: 'VERIFIER' as AdminRole })
  const [creating, setCreating] = useState(false)

  const signOut = useCallback(() => {
    setAdminToken(null)
    navigate('/admin/login', { replace: true })
  }, [navigate])

  const fail = useCallback(
    (err: unknown, fallback: string) => {
      if (err instanceof ApiError && err.status === 401) signOut()
      else setError(apiErrorMessage(err, fallback))
    },
    [signOut],
  )

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [list, me] = await Promise.all([adminApi.listAdmins(), adminApi.me()])
      setRows(list)
      setMeId(me.id)
    } catch (err) {
      fail(err, 'Could not load the team')
    } finally {
      setLoading(false)
    }
  }, [fail])

  useEffect(() => {
    void load()
  }, [load])

  async function update(row: AdminUser, body: Parameters<typeof adminApi.updateAdmin>[1], done: string) {
    setBusyId(row.id)
    setError(null)
    setNotice(null)
    try {
      const saved = await adminApi.updateAdmin(row.id, body)
      setRows((rs) => rs.map((r) => (r.id === saved.id ? saved : r)))
      setNotice(done)
      return true
    } catch (err) {
      fail(err, 'Could not save the change')
      return false
    } finally {
      setBusyId(null)
    }
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault()
    setCreating(true)
    setError(null)
    setNotice(null)
    try {
      const created = await adminApi.createAdmin(form)
      setRows((rs) => [...rs, created])
      setNotice(
        `${created.fullName} added as ${ROLE_LABEL[created.role]}. They sign in with a join code sent to their email.`,
      )
      setForm({ fullName: '', email: '', role: form.role })
    } catch (err) {
      fail(err, 'Could not add this person')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="admin-console min-h-screen bg-ink-100 lg:pl-64">
      <AdminHeader current="team" onSignOut={signOut} width="max-w-6xl">
        <button onClick={() => void load()} aria-label="Refresh" className="btn-ghost gap-1.5">
          <RefreshCw size={14} /> Refresh
        </button>
      </AdminHeader>

      <main className="mx-auto max-w-6xl space-y-5 px-4 py-6">
        <div>
          <h1 className="text-xl font-bold text-ink-900">Team &amp; roles</h1>
          <p className="mt-1 text-sm text-ink-500">
            Who can sign in to the ops console, and what each person may do. A change applies on their next click.
          </p>
        </div>

        <ErrorMessage message={error} />
        {notice && <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</p>}

        <form onSubmit={onCreate} className="card-pad space-y-3">
          <h2 className="flex items-center gap-2 font-semibold text-ink-900">
            <UserPlus size={16} /> Add a person
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="block">
              <span className="label label-required">Full name</span>
              <input
                required
                maxLength={200}
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                className="input"
              />
            </label>
            <label className="block">
              <span className="label label-required">Email</span>
              <input
                required
                type="email"
                autoComplete="off"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input"
              />
            </label>
            <label className="block">
              <span className="label label-required">Role</span>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as AdminRole })}
                className="input"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button type="submit" disabled={creating} className="btn-sun">
            {creating ? 'Adding...' : 'Add person'}
          </button>
        </form>

        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Last sign-in</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-ink-500">
                    Loading...
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                const self = row.id === meId
                const busy = busyId === row.id
                return (
                  <tr key={row.id} className="border-b border-ink-100 last:border-0 align-top">
                    <td className="px-4 py-3 font-medium text-ink-900">
                      {row.fullName}
                      {self && <span className="ml-1.5 text-xs font-normal text-ink-500">(you)</span>}
                    </td>
                    <td className="px-4 py-3 text-ink-700">
                      {emailFor === row.id ? (
                        <form
                          className="flex flex-wrap items-center gap-1.5"
                          onSubmit={(e) => {
                            e.preventDefault()
                            void update(row, { email: newEmail.trim() }, `${row.fullName}'s email is now ${newEmail.trim()}.`).then(
                              (ok) => ok && setEmailFor(null),
                            )
                          }}
                        >
                          <input
                            autoFocus
                            required
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            className="input !w-56 !py-1.5"
                          />
                          <button type="submit" disabled={busy} className="btn-sun !py-1.5">
                            Save
                          </button>
                          <button type="button" onClick={() => setEmailFor(null)} className="btn-ghost !py-1.5">
                            Cancel
                          </button>
                        </form>
                      ) : (
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="break-all">{row.email}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setEmailFor(row.id)
                              setNewEmail(row.email)
                            }}
                            className="text-xs font-semibold text-ink-500 underline underline-offset-2 hover:text-ink-900"
                          >
                            Edit
                          </button>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={row.role}
                        disabled={self || busy}
                        title={self ? 'You cannot change your own role' : undefined}
                        onChange={(e) => {
                          const role = e.target.value as AdminRole
                          void update(row, { role }, `${row.fullName} is now ${ROLE_LABEL[role]}.`)
                        }}
                        className="input !py-1.5"
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABEL[r]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          row.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {row.active ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-600">{fmt(row.lastLoginAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end gap-2">
                        {!self && (
                          <button
                            disabled={busy}
                            onClick={() =>
                              void update(
                                row,
                                { active: !row.active },
                                row.active ? `${row.fullName} can no longer sign in.` : `${row.fullName} can sign in again.`,
                              )
                            }
                            className="btn-ghost !py-1.5"
                          >
                            {row.active ? 'Disable' : 'Enable'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  )
}
